import { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useSketchStore } from '../../state/useSketchStore';
import {
    buildTerrainSolid, terrainFromContours, smooveTerrain,
    stampTerrain, drapeEdges, addTerrainDetail, isTerrain,
} from '../../core/sketch/terrain';
import { notify } from '../../utils/notify';
import type { InferenceResult } from '../../core/sketch/inference';
import { useSketchToolEnv } from './sketchInput';
import { registerSketchTool, type SketchHit } from './sketchToolBus';

/**
 * Sandbox tools (SketchUp terrain parity): From Contours, From Scratch,
 * Smoove, Stamp, Drape and Add Detail. Terrains are ordinary sketch solids
 * whose top shell is a TIN grid — every tool rewrites vertices or faces
 * through one history action.
 */

const DEFAULT_RADIUS = 4;

/** Terrain face pick guard: resolve the hit's solid and require a TIN top. */
function terrainOf(hit: SketchHit) {
    if (!hit.solidId) return null;
    const solid = useSketchStore.getState().solids[hit.solidId];
    if (!solid || !isTerrain(solid)) return null;
    return { solid, faceId: hit.faceId };
}

// ============================== FROM SCRATCH ==============================

/**
 * From Scratch: click the first grid corner, then the opposite corner (the
 * VCB can carry the CELL SIZE before the second click). A flat TIN slab is
 * born, ready for Smoove.
 */
export function useSketchFromScratchTool() {
    const env = useSketchToolEnv();
    const [corner, setCorner] = useState<THREE.Vector3 | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                setCursor(env.infer(hit, corner ? [corner] : [], null));
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const res = env.infer(hit, corner ? [corner] : [], null);
                if (!corner) {
                    setCorner(res.point.clone());
                    return;
                }
                const p = res.point.clone();
                const width = Math.abs(p.x - corner.x);
                const depth = Math.abs(p.y - corner.y);
                if (width < 1e-3 || depth < 1e-3) return;
                // VCB before the second click sets the cell size.
                const vcb = env.consumeVcb();
                const cell = vcb && vcb >= 0.5 ? vcb : 2;
                const cols = Math.min(20, Math.max(2, Math.round(width / cell)));
                const rows = Math.min(20, Math.max(2, Math.round(depth / cell)));
                const origin = new THREE.Vector3(
                    Math.min(corner.x, p.x),
                    Math.min(corner.y, p.y),
                    corner.z
                );
                const { solid } = buildTerrainSolid(origin, width, depth, cols, rows, () => origin.z);
                useSketchStore.getState().addSolid(solid, 'Sandbox Grid');
                setCorner(null);
                setCursor(null);
            },
        });
        return () => registerSketchTool(null);
    }, [env, corner]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            setCorner(null);
            setCursor(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { corner, cursor };
}

// ============================== FROM CONTOURS ==============================

/**
 * From Contours: clicks anywhere build a TIN through the loose edges
 * (drawn at several heights); the VCB carries the grid cell size.
 */
export function useSketchFromContoursTool() {
    const env = useSketchToolEnv();

    useEffect(() => {
        registerSketchTool({
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const edges = Object.values(useSketchStore.getState().edges);
                if (edges.length < 3) {
                    notify('Draw contour lines first (Line/Freehand at different heights).', 'warning');
                    return;
                }
                const vcb = env.consumeVcb();
                const cell = vcb && vcb >= 0.5 ? vcb : 2;
                const solid = terrainFromContours(edges, cell);
                if (!solid) {
                    notify('Contours could not be skinned.', 'warning');
                    return;
                }
                useSketchStore.getState().addSolid(solid, 'Terrain From Contours');
            },
        });
        return () => registerSketchTool(null);
    }, [env]);
    return {};
}

// ============================== SMOOVE ==============================

/**
 * Smoove: click the terrain center, optionally type the radius into the
 * VCB, then click again — the vertical distance between the two clicks
 * lifts/drops the surface with a smooth circular falloff.
 */
export function useSketchSmooveTool() {
    const env = useSketchToolEnv();
    const [center, setCenter] = useState<{ point: THREE.Vector3; radius: number; solidId: string } | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                setCursor(env.infer(hit, center ? [center.point] : [], null));
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const res = env.infer(hit, center ? [center.point] : [], null);
                if (!center) {
                    const terrain = terrainOf(hit);
                    if (!terrain) {
                        notify('Smoove needs a Sandbox terrain (From Scratch / From Contours).', 'warning');
                        return;
                    }
                    const vcb = env.consumeVcb();
                    setCenter({ point: res.point.clone(), radius: vcb && vcb > 0.1 ? vcb : DEFAULT_RADIUS, solidId: terrain.solid.id });
                    return;
                }
                const store = useSketchStore.getState();
                const solid = store.solids[center.solidId];
                if (solid) {
                    const delta = res.point.z - center.point.z;
                    if (Math.abs(delta) > 1e-9) {
                        store.updateSolid(solid.id, smooveTerrain(solid, center.point, center.radius, delta), 'Smoove');
                    }
                }
                setCenter(null);
                setCursor(null);
            },
        });
        return () => registerSketchTool(null);
    }, [env, center]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            setCenter(null);
            setCursor(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { center, cursor };
}

// ============================== STAMP ==============================

/**
 * Stamp: click the terrain, optionally type the radius into the VCB, then
 * click a height reference (any geometry) — the terrain flattens to that
 * height inside the pad radius.
 */
export function useSketchStampTool() {
    const env = useSketchToolEnv();
    const [center, setCenter] = useState<{ point: THREE.Vector3; radius: number; solidId: string } | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                setCursor(env.infer(hit, center ? [center.point] : [], null));
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const res = env.infer(hit, center ? [center.point] : [], null);
                if (!center) {
                    const terrain = terrainOf(hit);
                    if (!terrain) {
                        notify('Stamp needs a Sandbox terrain (From Scratch / From Contours).', 'warning');
                        return;
                    }
                    const vcb = env.consumeVcb();
                    setCenter({ point: res.point.clone(), radius: vcb && vcb > 0.1 ? vcb : DEFAULT_RADIUS, solidId: terrain.solid.id });
                    return;
                }
                const store = useSketchStore.getState();
                const solid = store.solids[center.solidId];
                if (solid) {
                    store.updateSolid(solid.id, stampTerrain(solid, center.point, center.radius, res.point.z), 'Stamp');
                }
                setCenter(null);
                setCursor(null);
            },
        });
        return () => registerSketchTool(null);
    }, [env, center]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            setCenter(null);
            setCursor(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { center, cursor };
}

// ============================== DRAPE ==============================

/**
 * Drape: click the terrain — every loose edge projects vertically onto the
 * TIN as a chain of on-surface segments.
 */
export function useSketchDrapeTool(): void {
    useEffect(() => {
        registerSketchTool({
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const store = useSketchStore.getState();
                const terrain = terrainOf(hit);
                if (!terrain) {
                    notify('Drape needs a Sandbox terrain (From Scratch / From Contours).', 'warning');
                    return;
                }
                const polylines = drapeEdges(terrain.solid, Object.values(store.edges));
                if (polylines.length === 0) {
                    notify('No loose edges to drape.', 'warning');
                    return;
                }
                polylines.forEach((chain) => {
                    for (let i = 1; i < chain.length; i++) {
                        store.addLooseSegment(chain[i - 1], chain[i], new THREE.Plane(new THREE.Vector3(0, 0, 1), 0), 'Drape');
                    }
                });
            },
        });
        return () => registerSketchTool(null);
    }, []);
}

// ============================== ADD DETAIL ==============================

/**
 * Add Detail: click a TIN triangle — the picked point splits it into three
 * sub-triangles (more vertices to Smoove later).
 */
export function useSketchAddDetailTool(): void {
    useEffect(() => {
        registerSketchTool({
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const terrain = terrainOf(hit);
                if (!terrain?.faceId) {
                    notify('Add Detail needs a Sandbox terrain triangle.', 'warning');
                    return;
                }
                const store = useSketchStore.getState();
                const next = addTerrainDetail(terrain.solid, terrain.faceId, hit.point);
                if (next) store.updateSolid(terrain.solid.id, next, 'Add Detail');
            },
        });
        return () => registerSketchTool(null);
    }, []);
}
